import { createParser } from 'eventsource-parser';

import { useAuth } from '@/context/auth-context';
import { useTenant } from '@/context/tenant-context';
import { publicRequest } from '@/lib/axios/public-request';
import {
  useAgentousPrivateRequest,
  usePrivateRequest,
  useUnauthenticatedAgentRequest,
} from '@/lib/axios/use-private-request';
import { type ChatStreamEvent } from '@/types/agent-chat';
import {
  type ActivityLogResponse,
  type AgentCategoriesResponse,
  type AIAgentsResponse,
  type AIAgentsSuiteResponse,
  type ChatRequest,
} from '@/types/agents';
import {
  type DemoRequestPayload,
  type DemoRequestResponse,
} from '@/types/pricing';
import {
  agenticService,
  agenticUserService,
} from '@/utils/api-service-controllers-route';
import { BASE_URL } from '@/utils/api-urls';
import { parseChatStreamEventData } from '@/utils/chat-stream-events';
import { extractErrorMessage } from '@/utils/error-utils';

import { CHAT_ENDPOINT } from './agent-chat-service';

/**
 * Generates a secure session ID using Web Crypto API
 * @returns A 64-character hex string representing a secure session ID
 */
export const generateSecureSessionId = (): string => {
  // Generate 32 random bytes (256 bits) for strong security
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);

  // Convert to hex string
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

export const useGetUserApi = () => {
  // For the /me endpoint, we don't need a tenant ID since it returns user info including tenants
  const axiosInstance = useAgentousPrivateRequest(BASE_URL, '', '');
  const getUser = async <T>(): Promise<T> => {
    const isReady = await axiosInstance.waitForReady(undefined, 3000);
    if (!isReady || !axiosInstance.current) {
      throw new Error('User profile service is still initializing.');
    }

    const res = await axiosInstance.current.get(
      `${agenticUserService}/accounts/me`
    );
    const payload = res.data?.data;
    if (!payload) {
      throw new Error('Unable to load user profile.');
    }

    return payload;
  };
  return getUser;
};

export const submitDemoRequest = async (
  payload: DemoRequestPayload
): Promise<DemoRequestResponse> => {
  try {
    const response = await publicRequest(BASE_URL).post<DemoRequestResponse>(
      `${agenticUserService}/users/request-demo`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Demo request submission failed:', error);
    throw error;
  }
};

// For other API calls that need tenant context
export const useGetAgentousTenantScopedApi = () => {
  const { tenantId } = useTenant();
  const axiosInstance = useAgentousPrivateRequest(BASE_URL, tenantId || '', '');

  return {
    get: async <T>(url: string, config?: any): Promise<T> => {
      const res = await axiosInstance.current?.get(url, config);
      return res?.data?.data;
    },
    post: async <T>(url: string, data?: any, config?: any): Promise<T> => {
      const res = await axiosInstance.current?.post(url, data, config);
      return res?.data?.data;
    },
    put: async <T>(url: string, data?: any, config?: any): Promise<T> => {
      const res = await axiosInstance.current?.put(url, data, config);
      return res?.data?.data;
    },
    delete: async <T>(url: string, config?: any): Promise<T> => {
      const res = await axiosInstance.current?.delete(url, config);
      return res?.data?.data;
    },
  };
};

// Hooks for AI agents API
const AGENTS_ENDPOINT = `${agenticService}/ai-agents/public`;

export const useAIAgentsApi = () => {
  const getAIAgents = async (): Promise<AIAgentsResponse> => {
    try {
      const { data } = await publicRequest(BASE_URL).get(AGENTS_ENDPOINT);
      return (
        data || {
          status: false,
          message: 'No data',
          data: { aiAgents: [], total: 0, page: 1, pageSize: 20 },
        }
      );
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  };

  return getAIAgents;
};

export const useAIAgentsCategoriesApi = () => {
  const getAIAgents = async (): Promise<AgentCategoriesResponse> => {
    try {
      const { data } = await publicRequest(BASE_URL).get(
        `${AGENTS_ENDPOINT}/categories`
      );
      return (
        data || {
          status: false,
          message: 'No data',
          data: [],
        }
      );
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  };

  return getAIAgents;
};

export const useAIAgentSuiteApi = () => {
  const getAIAgentsSuite = async (): Promise<AIAgentsSuiteResponse> => {
    try {
      const { data } = await publicRequest(BASE_URL).get(
        `${agenticService}/ai-agent-suites/public`
      );
      return (
        data || {
          status: false,
          message: 'No data',
          data: { aiAgentSuites: [], total: 0, page: 1, pageSize: 20 },
        }
      );
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  };

  return getAIAgentsSuite;
};

export const useClaimAgentSuiteApi = () => {
  const axiosInstance = usePrivateRequest(BASE_URL);

  const claimAgentsSuite = async (agentSuiteKey: string): Promise<any> => {
    try {
      const res = await axiosInstance.current?.post(
        `${agenticUserService}/tenants/claim-agent-suite`,
        {
          data: agentSuiteKey,
        }
      );
      return (
        res?.data || {
          status: false,
          message: 'No data',
        }
      );
    } catch (error: unknown) {
      throw new Error(extractErrorMessage(error));
    }
  };

  return claimAgentsSuite;
};

export const useFileActivityLogApi = () => {
  const axiosInstance = usePrivateRequest(BASE_URL);

  const getActivityLog = async (
    fileRef: string
  ): Promise<ActivityLogResponse> => {
    try {
      const res = await axiosInstance.current?.get(
        `${agenticService}/file-activity-log/${fileRef}`
      );
      return (
        res?.data || {
          status: false,
          message: 'No data',
        }
      );
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  };

  return getActivityLog;
};

const useRegisApi = () => {
  const { tenantId } = useTenant();
  const { isAuthenticated } = useAuth();
  const agentKey = 'regis';

  // Always call both hooks to maintain consistent hook order
  const authenticatedRequest = useAgentousPrivateRequest(
    BASE_URL,
    tenantId || '',
    agentKey
  );
  const unauthenticatedRequest = useUnauthenticatedAgentRequest(
    BASE_URL,
    agentKey
  );

  // Return the appropriate request based on authentication status
  return isAuthenticated ? authenticatedRequest : unauthenticatedRequest;
};

export const useRegisChatApi = () => {
  const requestRef = useRegisApi();

  const chatWithRegis = async (
    payload: ChatRequest,
    options?: {
      signal?: AbortSignal;
      onEvent?: (event: ChatStreamEvent) => void;
    }
  ): Promise<string> => {
    try {
      const axiosInstance = requestRef.current;
      if (!axiosInstance) {
        throw new Error('Axios instance not initialized');
      }

      const extractHeaders = (source: unknown) => {
        if (!source || typeof source !== 'object') return {};
        return Object.entries(source as Record<string, unknown>).reduce(
          (acc, [key, value]) => {
            if (typeof value === 'string') {
              acc[key] = value;
            }
            return acc;
          },
          {} as Record<string, string>
        );
      };

      const axiosDefaults = axiosInstance.defaults.headers || {};
      const mergedHeaders = {
        ...extractHeaders(axiosDefaults.common),
        ...extractHeaders(axiosDefaults.post),
        ...extractHeaders(axiosDefaults),
      };

      const response = await fetch(`${BASE_URL}${CHAT_ENDPOINT}`, {
        method: 'POST',
        headers: {
          ...mergedHeaders,
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
          Connection: 'keep-alive',
          'Content-Type': 'application/json',
          'X-Active-Agent': 'regis',
        },
        body: JSON.stringify({ ...payload, stream: true }),
        cache: 'no-store',
        signal: options?.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('text/event-stream')) {
        return response.text();
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Regis chat stream response did not include a body.');
      }

      const decoder = new TextDecoder('utf-8');
      let accumulatedMessage = '';
      let finalMessage = '';

      const parser = createParser({
        onEvent(event) {
          const streamEvent = parseChatStreamEventData(event.data, event.event);
          if (!streamEvent) return;

          if (streamEvent.type === 'delta' && streamEvent.text) {
            accumulatedMessage += streamEvent.text;
          }

          if (streamEvent.type === 'final') {
            finalMessage = streamEvent.text ?? accumulatedMessage;
          }

          options?.onEvent?.(streamEvent);
        },
      });

      try {
        while (true) {
          if (options?.signal?.aborted) {
            throw new DOMException('The operation was aborted.', 'AbortError');
          }

          const { done, value } = await reader.read();
          if (done) break;

          parser.feed(decoder.decode(value, { stream: true }));
        }

        const trailingText = decoder.decode();
        if (trailingText) {
          parser.feed(trailingText);
        }
      } finally {
        reader.releaseLock();
      }

      return finalMessage || accumulatedMessage;
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  };

  return chatWithRegis;
};
